import React, { useEffect, useMemo, useState } from "react";
import { Share2, Check, Copy, Link as LinkIcon, X, MapPin, ExternalLink } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const TIDS = {
  root: "share-chapter-dialog",
  close: "share-chapter-close",
  selectAll: "share-chapter-select-all",
  clear: "share-chapter-clear",
  story: (id) => `share-chapter-story-${id}`,
  storyCheckbox: (id) => `share-chapter-story-checkbox-${id}`,
  generate: "share-chapter-generate",
  copy: "share-chapter-copy",
  nativeShare: "share-chapter-native-share",
  result: "share-chapter-result",
};

/**
 * Lets a user share a Chapter publicly. Two modes:
 *   • "share entire chapter"  → empty story_ids → new stories appear automatically
 *   • "share selected"        → user picks specific stories via checkboxes
 *
 * The dialog is two-pane:
 *   step 1 — pick stories
 *   step 2 — copy / share the resulting link
 */
export default function ShareChapterDialog({ year, stories, onClose }) {
  // Default: every story selected (most common case).
  const [selected, setSelected] = useState(() => new Set(stories.map((s) => s.id)));
  const [share, setShare] = useState(null);   // { url, share_url, slug, all_stories }
  const [busy, setBusy] = useState(false);

  const allSelected = selected.size === stories.length && stories.length > 0;
  const noneSelected = selected.size === 0;

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(stories.map((s) => s.id)));
  const clearAll = () => setSelected(new Set());

  const generate = async () => {
    if (noneSelected) {
      toast.error("Pick at least one story to share — or hit 'select all'.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        year,
        // empty list = "entire chapter" so new stories auto-appear
        story_ids: allSelected ? [] : Array.from(selected),
      };
      const { data } = await api.post("/chapters/share", payload);
      setShare(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't create share");
    } finally { setBusy(false); }
  };

  const copy = async () => {
    if (!share?.url) return;
    try {
      await navigator.clipboard.writeText(share.url);
      toast.success("Link copied — paste it anywhere");
    } catch {
      toast.error("Couldn't copy — long-press the link to copy manually");
    }
  };

  const nativeShare = async () => {
    if (!share?.share_url) return;
    if (!navigator.share) { copy(); return; }
    try {
      await navigator.share({
        title: `Chapter ${year} · TravelBio`,
        text: `Read along on my Chapter ${year} travel journal`,
        url: share.share_url,
      });
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Share cancelled");
    }
  };

  return (
    <div
      data-testid={TIDS.root}
      className="fixed inset-0 z-[70] bg-tb-navy-deep/75 flex items-end md:items-center justify-center p-3 overflow-y-auto"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-tb-cream rounded-2xl border-2 border-tb-gold max-w-md w-full max-h-[92vh] overflow-hidden flex flex-col shadow-2xl"
      >
        <div className="bg-tb-navy text-tb-gold-bright px-4 py-3 flex items-center justify-between shrink-0">
          <span className="font-hand text-xl flex items-center gap-2">
            <Share2 className="w-5 h-5" /> share chapter {year}
          </span>
          <button
            data-testid={TIDS.close}
            onClick={onClose}
            className="font-hand text-tb-gold tb-tap inline-flex items-center gap-1"
          >
            <X className="w-4 h-4" /> close
          </button>
        </div>

        {!share ? (
          <>
            <div className="px-4 pt-3 pb-2 flex items-center justify-between gap-2 border-b border-tb-cream-3 shrink-0">
              <span className="font-hand text-tb-navy/70 text-sm italic">
                {selected.size} of {stories.length} selected
                {allSelected && (
                  <span className="ml-1 text-tb-coral not-italic">· entire chapter</span>
                )}
              </span>
              <div className="flex gap-2">
                <button
                  data-testid={TIDS.selectAll}
                  onClick={selectAll}
                  className="px-3 py-1 rounded-full bg-tb-coral text-white font-hand text-xs tb-tap hover:bg-tb-coral/90"
                >
                  select all
                </button>
                <button
                  data-testid={TIDS.clear}
                  onClick={clearAll}
                  disabled={noneSelected}
                  className="px-3 py-1 rounded-full bg-tb-cream-2 border-2 border-tb-cream-3 text-tb-navy font-hand text-xs tb-tap disabled:opacity-50"
                >
                  clear
                </button>
              </div>
            </div>

            <ul className="flex-1 overflow-y-auto px-3 py-2 space-y-1.5">
              {stories.length === 0 && (
                <li className="font-hand text-tb-navy/55 italic text-center py-6">
                  no stories in this chapter yet
                </li>
              )}
              {stories.map((s) => {
                const isSelected = selected.has(s.id);
                return (
                  <li key={s.id}>
                    <label
                      data-testid={TIDS.story(s.id)}
                      className={`flex items-center gap-3 px-3 py-2 rounded-xl border-2 cursor-pointer tb-tap ${
                        isSelected
                          ? "bg-tb-coral-soft border-tb-coral"
                          : "bg-tb-cream-2 border-tb-cream-3 hover:border-tb-coral/50"
                      }`}
                    >
                      <input
                        data-testid={TIDS.storyCheckbox(s.id)}
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggle(s.id)}
                        className="w-5 h-5 accent-tb-coral cursor-pointer shrink-0"
                      />
                      {s.image && (
                        <img
                          src={s.image} alt=""
                          className="w-12 h-12 rounded-lg object-cover border-2 border-tb-cream-3 shrink-0"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-serif-it text-tb-navy text-lg truncate">
                          {s.trip_name}
                        </div>
                        <div className="font-hand text-tb-navy/55 text-xs flex items-center gap-1 truncate">
                          <MapPin className="w-3 h-3" /> {s.country}
                        </div>
                      </div>
                      {isSelected && <Check className="w-4 h-4 text-tb-coral shrink-0" />}
                    </label>
                  </li>
                );
              })}
            </ul>

            <div className="p-3 border-t border-tb-cream-3 shrink-0">
              <button
                data-testid={TIDS.generate}
                disabled={noneSelected || busy}
                onClick={generate}
                className="w-full py-3 rounded-xl bg-tb-navy text-tb-gold-bright border-2 border-tb-gold font-hand text-lg tb-tap disabled:opacity-40 flex items-center justify-center gap-2"
              >
                {busy ? "creating link…" : (
                  <>
                    <LinkIcon className="w-4 h-4" />
                    {allSelected ? `share entire chapter (${stories.length})` : `share ${selected.size} stor${selected.size === 1 ? "y" : "ies"}`}
                  </>
                )}
              </button>
              {allSelected && (
                <p className="font-hand text-tb-navy/55 italic text-xs text-center mt-2">
                  ✦ stories you add later will appear in this link automatically
                </p>
              )}
            </div>
          </>
        ) : (
          <div data-testid={TIDS.result} className="flex-1 overflow-y-auto p-4 space-y-4">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-tb-coral-soft border-2 border-tb-coral mb-2">
                <Check className="w-7 h-7 text-tb-coral" />
              </div>
              <p className="font-serif-it text-tb-navy text-2xl">your chapter is shareable</p>
              <p className="font-hand text-tb-navy/65 italic text-sm mt-1">
                anyone with the link can read along — no login required
              </p>
            </div>

            <div className="rounded-xl border-2 border-tb-cream-3 bg-tb-cream-2/60 p-3 break-all font-mono text-xs text-tb-navy/85">
              {share.url}
            </div>

            <div className="grid grid-cols-2 gap-2">
              <button
                data-testid={TIDS.copy}
                onClick={copy}
                className="py-2.5 rounded-xl bg-tb-cream-2 border-2 border-tb-cream-3 hover:border-tb-coral font-hand text-base text-tb-navy tb-tap flex items-center justify-center gap-2"
              >
                <Copy className="w-4 h-4" /> copy link
              </button>
              <button
                data-testid={TIDS.nativeShare}
                onClick={nativeShare}
                className="py-2.5 rounded-xl bg-tb-navy text-tb-gold-bright border-2 border-tb-gold font-hand text-base tb-tap flex items-center justify-center gap-2"
              >
                <Share2 className="w-4 h-4" /> share
              </button>
            </div>

            <a
              href={share.url}
              target="_blank"
              rel="noopener noreferrer"
              className="block text-center font-hand text-tb-navy/65 italic text-sm hover:text-tb-coral"
            >
              <ExternalLink className="w-3.5 h-3.5 inline -mt-0.5 mr-1" />
              preview the public page
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
